import { createSlice } from '@reduxjs/toolkit'

/**
 * Storage keys.
 *
 * `guestSession` now holds ONLY a live session and is removed as soon as that
 * session stops being valid. Previously it was deliberately left behind after
 * expiry so the post-stay feedback modal could still find the hotel and room —
 * which meant a departed guest's session id, room number and hotel name sat on
 * the device indefinitely and were rehydrated on the next app open.
 *
 * The feedback flow now gets its own key carrying the minimum it needs.
 */
const SESSION_KEY = 'guestSession'
const FEEDBACK_KEY = 'guestFeedbackContext'

// Matches FEEDBACK_GRACE_MS in the backend's middleware/guest.js. Past this the
// server rejects the session id anyway, so there is no reason to keep it.
const FEEDBACK_GRACE_MS = 60 * 60 * 1000

const safeGet = (key) => {
  try {
    return localStorage.getItem(key)
  } catch {
    // Storage can be unavailable in private mode or inside an iframe.
    return null
  }
}

const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, value)
  } catch (error) {
    console.error(`Error writing ${key} to storage:`, error)
  }
}

const safeRemove = (key) => {
  try {
    localStorage.removeItem(key)
  } catch {
    // Ignore storage errors
  }
}

/**
 * Single source of truth for the persisted session shape.
 *
 * This used to be duplicated across four reducers, and they disagreed:
 * updateSessionExpiry omitted feedbackStatus, which silently reset a dismissed
 * session back to pending and re-surfaced the previous guest's data.
 */
const persistSession = (state) => {
  safeSet(
    SESSION_KEY,
    JSON.stringify({
      sessionId: state.sessionId,
      hotelId: state.hotelId,
      roomId: state.roomId,
      roomNumber: state.roomNumber,
      hotelName: state.hotelName,
      siteId: state.siteId,
      sessionExpiry: state.sessionExpiry,
      status: state.status,
      theme: state.theme,
      subscriptionTier: state.subscriptionTier,
      hasActiveSession: state.hasActiveSession,
      feedbackStatus: state.feedbackStatus,
    })
  )
}

/**
 * Hands the feedback modal the minimum it needs to submit against a session
 * that has just ended: the session id, the hotel, and the branding to render
 * with. Notably no roomId, siteId or subscription details.
 */
const persistFeedbackContext = (state) => {
  if (!state.sessionId || !state.hotelId) return
  safeSet(
    FEEDBACK_KEY,
    JSON.stringify({
      sessionId: state.sessionId,
      hotelId: state.hotelId,
      theme: state.theme,
      endedAt: new Date().toISOString(),
    })
  )
}

const clearSessionStorage = () => {
  safeRemove(SESSION_KEY)
}

const clearFeedbackContext = () => {
  safeRemove(FEEDBACK_KEY)
}

/**
 * Loads a live session from storage. Anything expired is discarded here rather
 * than being kept around, and the feedback context is promoted in its place.
 */
const loadSessionFromStorage = () => {
  const stored = safeGet(SESSION_KEY)
  if (!stored) return null

  try {
    const parsed = JSON.parse(stored)
    if (parsed.sessionExpiry && new Date() < new Date(parsed.sessionExpiry)) {
      return parsed
    }
    // Expired. Hand the feedback flow what it needs, then drop the session blob.
    if (parsed.sessionId && parsed.hotelId) {
      const fbStatus = parsed.feedbackStatus || 'none'
      if (fbStatus !== 'dismissed' && fbStatus !== 'submitted') {
        safeSet(
          FEEDBACK_KEY,
          JSON.stringify({
            sessionId: parsed.sessionId,
            hotelId: parsed.hotelId,
            theme: parsed.theme || null,
            endedAt: parsed.sessionExpiry || new Date().toISOString(),
          })
        )
      }
    }
    clearSessionStorage()
    return null
  } catch (error) {
    if (error.name !== 'SecurityError') {
      console.error('Error loading session from storage:', error)
    }
    clearSessionStorage()
    return null
  }
}

/**
 * Loads pending feedback context, if it hasn't gone stale.
 */
const loadFeedbackContext = () => {
  const stored = safeGet(FEEDBACK_KEY)
  if (!stored) return null

  try {
    const parsed = JSON.parse(stored)
    if (!parsed.sessionId || !parsed.hotelId) {
      clearFeedbackContext()
      return null
    }
    const endedAt = parsed.endedAt ? new Date(parsed.endedAt).getTime() : 0
    if (!endedAt || Date.now() - endedAt > FEEDBACK_GRACE_MS) {
      // The server would reject this session id by now.
      clearFeedbackContext()
      return null
    }
    return parsed
  } catch {
    clearFeedbackContext()
    return null
  }
}

const initialState = {
  hasActiveSession: false,
  isLoading: false,
  sessionId: null,
  sessionExpiry: null,
  hotelId: null,
  roomId: null,
  hotelName: null,
  siteId: null,
  roomNumber: null,
  status: null,
  theme: null,
  subscriptionTier: 'Express', // Hotel's subscription tier
  feedbackStatus: 'none', // 'none', 'pending', 'dismissed', 'submitted'
  // Set once the server has confirmed the cached session is still live.
  isSessionVerified: false,
  error: null,
}

const buildInitialState = () => {
  const savedSession = loadSessionFromStorage()

  if (savedSession) {
    return {
      ...initialState,
      ...savedSession,
      hasActiveSession: true,
      // Storage alone is not proof. A session ended server-side still looks
      // valid here until getCurrentSession confirms it (see useSessionGuard).
      isSessionVerified: false,
      isLoading: false,
    }
  }

  const feedbackContext = loadFeedbackContext()
  if (feedbackContext) {
    return {
      ...initialState,
      sessionId: feedbackContext.sessionId,
      hotelId: feedbackContext.hotelId,
      theme: feedbackContext.theme || null,
      hasActiveSession: false,
      feedbackStatus: 'pending',
      isLoading: false,
    }
  }

  return initialState
}

const authSlice = createSlice({
  name: 'auth',
  initialState: buildInitialState(),
  reducers: {
    sessionStart: (state) => {
      state.isLoading = true
      state.error = null
    },
    sessionSuccess: (state, action) => {
      const { sessionId, hotelId, roomId, roomNumber, hotelName, siteId, expiresAt, status, theme, subscriptionTier } = action.payload

      state.hasActiveSession = true
      state.isLoading = false
      state.sessionId = sessionId
      state.hotelId = hotelId
      state.roomId = roomId
      state.roomNumber = roomNumber
      state.hotelName = hotelName
      state.siteId = siteId
      state.sessionExpiry = expiresAt
      state.status = status
      state.theme = theme
      // Only fall back to 'Express' when the caller genuinely has no tier,
      // otherwise a caller that simply omits it silently downgrades features.
      state.subscriptionTier = subscriptionTier || state.subscriptionTier || 'Express'
      // The server just issued this session, so it's verified by definition.
      state.isSessionVerified = true
      // A fresh session starts with a clean feedback slate.
      state.feedbackStatus = 'none'
      state.error = null

      // A new session supersedes any pending feedback from the previous one.
      clearFeedbackContext()
      persistSession(state)
    },
    sessionFailure: (state, action) => {
      state.isLoading = false
      state.error = action.payload
      state.hasActiveSession = false
      state.isSessionVerified = false

      clearSessionStorage()
    },
    sessionExpired: (state) => {
      // Preserve what feedback needs BEFORE wiping the rest.
      persistFeedbackContext(state)

      state.hasActiveSession = false
      state.sessionExpiry = null
      state.status = 'Expired'
      state.isSessionVerified = false
      state.roomId = null
      state.roomNumber = null
      state.siteId = null
      state.hotelName = null
      state.error = 'Session has expired. Please scan the QR code again.'
      if (state.feedbackStatus === 'none') {
        state.feedbackStatus = 'pending'
      }

      // The session is dead — don't leave it on the device.
      clearSessionStorage()
    },
    endSession: () => {
      // Feedback has been handled by the time this runs, so clear both keys.
      clearSessionStorage()
      clearFeedbackContext()
      return { ...initialState }
    },
    clearError: (state) => {
      state.error = null
    },
    updateSessionExpiry: (state, action) => {
      state.sessionExpiry = action.payload
      persistSession(state)
    },
    // Marks the cached session as confirmed-live by the server.
    sessionVerified: (state, action) => {
      state.isSessionVerified = true
      const { expiresAt, status } = action.payload || {}
      if (expiresAt) state.sessionExpiry = expiresAt
      if (status) state.status = status
      persistSession(state)
    },
    // Restore a session that came from storage. Deliberately leaves
    // isSessionVerified false until the server confirms it.
    restoreSession: (state, action) => {
      const sessionData = action.payload
      state.hasActiveSession = true
      state.sessionId = sessionData.sessionId
      state.hotelId = sessionData.hotelId
      state.roomId = sessionData.roomId
      state.roomNumber = sessionData.roomNumber
      state.hotelName = sessionData.hotelName
      state.siteId = sessionData.siteId
      state.sessionExpiry = sessionData.sessionExpiry
      state.status = sessionData.status
      state.theme = sessionData.theme
      state.subscriptionTier = sessionData.subscriptionTier || 'Express'
      state.feedbackStatus = sessionData.feedbackStatus || 'none'
      state.isSessionVerified = false
      state.isLoading = false
      state.error = null
    },
    // Add action to update subscription tier and theme separately
    updateHotelData: (state, action) => {
      const { theme, subscriptionTier } = action.payload
      if (theme) state.theme = theme
      if (subscriptionTier) state.subscriptionTier = subscriptionTier

      persistSession(state)
    },
    // Add action to update feedback status
    updateFeedbackStatus: (state, action) => {
      const { feedbackStatus } = action.payload
      state.feedbackStatus = feedbackStatus

      if (feedbackStatus === 'dismissed' || feedbackStatus === 'submitted') {
        clearFeedbackContext()
      } else if (!state.hasActiveSession) {
        // Session already gone — keep the context alive for the modal.
        persistFeedbackContext(state)
      }

      if (state.hasActiveSession) {
        persistSession(state)
      }
    },
  },
})

export const {
  sessionStart,
  sessionSuccess,
  sessionFailure,
  sessionExpired,
  endSession,
  clearError,
  updateSessionExpiry,
  sessionVerified,
  restoreSession,
  updateHotelData,
  updateFeedbackStatus,
} = authSlice.actions

export default authSlice.reducer

// Selectors
export const selectAuth = (state) => state.auth
export const selectHasActiveSession = (state) => state.auth.hasActiveSession
export const selectSessionExpiry = (state) => state.auth.sessionExpiry
export const selectIsSessionVerified = (state) => state.auth.isSessionVerified
export const selectIsSessionExpired = (state) => {
  if (!state.auth.sessionExpiry) return false
  return new Date() >= new Date(state.auth.sessionExpiry)
}
export const selectCurrentGuest = (state) => ({
  sessionId: state.auth.sessionId,
  hotelId: state.auth.hotelId,
  roomId: state.auth.roomId,
  roomNumber: state.auth.roomNumber,
  hotelName: state.auth.hotelName,
  siteId: state.auth.siteId,
  status: state.auth.status,
  expiresAt: state.auth.sessionExpiry,
})

// Selector for theme from auth state
export const selectThemeFromAuth = (state) => state.auth.theme

/**
 * Reads a live session straight out of storage.
 *
 * Returns null for an expired session rather than handing it back — callers
 * were using the old behaviour to restore dead sessions into an "active" state.
 */
export const selectSessionFromStorage = () => {
  const stored = safeGet(SESSION_KEY)
  if (!stored) return null

  try {
    const parsed = JSON.parse(stored)
    if (parsed.sessionExpiry && new Date() < new Date(parsed.sessionExpiry)) {
      return parsed
    }
    return null
  } catch {
    return null
  }
}
